import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Textarea } from 'src/design/atoms/Textarea';
import { Tooltip } from 'src/design/atoms/Tooltip';
import {
  IconBulb,
  IconBulbFilled,
  IconPlayerStop,
  IconSend,
} from '@tabler/icons-react';
import { useMergedRef } from '@mantine/hooks';
import React, { useEffect, useRef, useState } from 'react';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { IComposerDraft } from 'src/hooks/useChat';

export interface IComposerProps {
  streaming: boolean;
  /** Disables sending without showing the stop button, e.g. while a fork is pending. */
  busy?: boolean;
  onSend: (text: string) => void;
  onStop: () => void;
  /** Replaces the typed text and focuses the composer with the caret at the end. */
  draft?: IComposerDraft | null;
  onDraftApplied?: (nonce: number) => void;
  thinkHarder: boolean;
  onThinkHarderChange: (value: boolean) => void;
}

/** @registry Chat message textarea over a toolbar with a "Think harder" toggle (cost hint when on) and the send/stop button; Enter sends (Shift+Enter for newline), swaps to a stop icon while streaming, disables sending while `busy`, and takes over a `draft` (e.g. a forked message) when one is given. */
export const Composer = React.memo(
  React.forwardRef<HTMLTextAreaElement, IComposerProps>(
    (
      {
        streaming,
        busy,
        onSend,
        onStop,
        draft,
        onDraftApplied,
        thinkHarder,
        onThinkHarderChange,
      },
      ref,
    ) => {
      const [value, setValue] = useState('');
      const textareaRef = useRef<HTMLTextAreaElement>(null);
      const mergedRef = useMergedRef(ref, textareaRef);

      // Keyed on the draft's nonce rather than its text, and read only after
      // the drafted value is committed, so the caret lands after the new text.
      const [caretToEndNonce, setCaretToEndNonce] = useState<number | null>(
        null,
      );

      useEffect(() => {
        if (!draft) return;
        setValue(draft.text);
        setCaretToEndNonce(draft.nonce);
        onDraftApplied?.(draft.nonce);
      }, [draft]);

      useEffect(() => {
        const textarea = textareaRef.current;
        if (caretToEndNonce === null || !textarea) return;
        textarea.focus();
        textarea.setSelectionRange(
          textarea.value.length,
          textarea.value.length,
        );
      }, [caretToEndNonce]);

      const handleSend = () => {
        if (!value.trim() || streaming || busy) return;
        onSend(value);
        setValue('');
      };

      return (
        <Stack gap="xs" p="sm">
          <Textarea
            ref={mergedRef}
            placeholder="Ask F.I.R.E.…"
            autosize
            minRows={1}
            maxRows={6}
            value={value}
            onChange={(event) => setValue(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                handleSend();
              }
            }}
          />
          <Group justify="space-between" align="center" wrap="nowrap" gap="xs">
            <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
              <Tooltip
                label="More careful answers for complex questions. Replies take longer and use more of your daily usage."
                events={{ hover: true, focus: true, touch: false }}
                multiline
                maw={260}
              >
                <BasicButton
                  size="compact-xs"
                  variant={thinkHarder ? 'light' : 'subtle'}
                  color={thinkHarder ? undefined : 'gray'}
                  leftSection={
                    thinkHarder ? (
                      <IconBulbFilled size={14} />
                    ) : (
                      <IconBulb size={14} />
                    )
                  }
                  aria-pressed={thinkHarder}
                  onClick={() => onThinkHarderChange(!thinkHarder)}
                  style={{ flexShrink: 0 }}
                >
                  Think harder
                </BasicButton>
              </Tooltip>
              {thinkHarder && (
                <Text size="xs" c="dimmed">
                  Uses more of your daily usage
                </Text>
              )}
            </Group>
            {streaming ? (
              <BasicActionIcon
                variant="filled"
                color="red"
                size="lg"
                label="Stop generating"
                onClick={onStop}
              >
                <IconPlayerStop size={18} />
              </BasicActionIcon>
            ) : (
              <BasicActionIcon
                variant="filled"
                size="lg"
                label="Send message"
                disabled={!value.trim() || busy}
                onClick={handleSend}
              >
                <IconSend size={18} />
              </BasicActionIcon>
            )}
          </Group>
        </Stack>
      );
    },
  ),
);
